import {DestroyRef, inject, Injectable, NgZone} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Auth} from '@angular/fire/auth';
import {filter} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '../../../environments/environment';

/**
 * Dwell-time analytics tracker (Angular SPA port).
 *
 * Measures how long a user actively spends on each route so we can see which
 * features get used. Adapted from the legacy multi-page module
 * `src/assets/js/analytics/dwell-tracker.js`:
 *
 *  - **Per route**: on every {@link NavigationEnd} the accrued active-time for the
 *    *previous* URL is flushed (sendBeacon) and counters reset for the new URL.
 *  - **Active-time only**: tracking pauses on tab/window blur and stops after 10 s
 *    of inactivity; any of scroll/click/keydown/touch/wheel resumes it.
 *  - **Authorized**: the dwell endpoint is a dedicated authenticated `onRequest`
 *    function. Since `sendBeacon` cannot set headers, the current Firebase ID token
 *    travels in the body; it is kept fresh via `onIdTokenChanged` (read synchronously
 *    at send time). Unauthenticated samples (e.g. login page) are dropped.
 *  - **Bounded payload**: after {@link MAX_LOG_ENTRIES} activity entries the data is
 *    auto-flushed and measuring continues — the log never grows without bound and
 *    nothing is silently dropped.
 *  - **No CORS preflight**: the beacon body is sent as a plain string
 *    (`text/plain`), a CORS-safelisted content type — the function `JSON.parse`s it.
 *  - **Zone-safe**: all high-frequency DOM listeners run via
 *    {@link NgZone.runOutsideAngular} so they never trigger change detection.
 *  - **localhost = debug only**: on localhost nothing is sent — payloads are logged
 *    to the console for debugging (intentional, see {@link DwellTracker.debug}).
 *
 * Initialised once from `LayoutContainerComponent.ngOnInit` via {@link init}.
 */

const API_URL = environment.dwellTracker.url;
const INACTIVITY_MS = 10_000;
/** Activity entries per beacon before an automatic flush-and-continue. Bounds payload size. */
const MAX_LOG_ENTRIES = 100;
const VISITOR_COOKIE = 'explicaire_visitor_id';
const COOKIE_DOMAIN = '.guideglare.com';

interface ActivityEntry {
    action: 'resume' | 'pause' | 'reset_continue';
    reason: string;
    timestamp: string;
    duration: number | null;
}

interface AnalyticsData {
    userAgent: string;
    language: string;
    languages: readonly string[] | string;
    platform: string;
    cookieEnabled: boolean | string;
    screenWidth: number | string;
    screenHeight: number | string;
    devicePixelRatio: number | string;
    viewportWidth: number | string;
    viewportHeight: number | string;
    hardwareConcurrency: number | string;
    touchSupported: boolean | string;
    timezone: string;
    referrer: string;
}

@Injectable({providedIn: 'root'})
export class DwellTrackerService {
    private readonly ngZone = inject(NgZone);
    private readonly router = inject(Router);
    private readonly auth = inject(Auth);
    private readonly destroyRef = inject(DestroyRef);

    private initialized = false;
    private readonly debug = ['localhost', '127.0.0.1'].includes(window.location.hostname);

    /** Latest Firebase ID token, kept fresh by `onIdTokenChanged`; null when signed out. */
    private currentIdToken: string | null = null;

    /** Guards against re-entrant flushes triggered from within {@link addActivityLog}. */
    private flushing = false;

    /** URL the currently-accruing time belongs to (full href captured at route start). */
    private currentUrl = '';

    private activeTime = 0;
    private lastStartTime = Date.now();
    private isTracking = false;
    private activityLog: ActivityEntry[] = [];
    private inactivityTimer: number | null = null;

    /** Teardown callbacks (removeEventListener) for every DOM listener registered in {@link setupListeners}. */
    private readonly teardown: (() => void)[] = [];

    /** Idempotent. Starts tracking and wires up router + DOM listeners. */
    init(): void {
        if (this.initialized) return;
        this.initialized = true;

        if (this.debug) {
            console.info('[DwellTracker] Debug mode on host:', window.location.hostname);
        }

        this.currentUrl = window.location.href;
        this.lastStartTime = Date.now();

        const isVisible = !document.hidden && document.visibilityState === 'visible';
        const hasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        if (isVisible && hasFocus) this.resumeTracking('init');

        this.setupListeners();
        this.ensureVisitorId();

        // Keep the latest ID token in memory so the beacon payload can carry it
        // synchronously. onIdTokenChanged fires on sign-in/out and every refresh,
        // so the held token is always valid.
        const unsubToken = this.auth.onIdTokenChanged(async user => {
            this.currentIdToken = user ? await user.getIdToken() : null;
        });
        this.destroyRef.onDestroy(() => unsubToken());

        // Per-route: flush the previous route's dwell, then start measuring the new one.
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.rotateRoute());

        // Flush whatever is pending when the app/service is torn down.
        this.destroyRef.onDestroy(() => {
            this.pauseTracking('destroy');
            this.sendDwellTime(this.currentUrl);
            this.clearInactivityCountdown();
            this.teardown.forEach(fn => fn());
            this.teardown.length = 0;
        });
    }

    /* --- per-route rotation --- */
    private rotateRoute(): void {
        this.pauseTracking('navigation');
        this.sendDwellTime(this.currentUrl);
        this.resetCounters();
        this.currentUrl = window.location.href;
        this.resumeTracking('navigation');
    }

    /* --- logging helpers --- */
    private log(...args: unknown[]): void {
        if (this.debug) console.log('[DwellTracker]', ...args);
    }

    private addActivityLog(action: ActivityEntry['action'], reason: string, ts: number, duration: number | null = null): void {
        this.activityLog.push({action, reason, timestamp: new Date(ts).toISOString(), duration});
        // Bound the payload: at the threshold, flush what we have and keep measuring
        // (instead of silently dropping the oldest entries).
        if (this.activityLog.length >= MAX_LOG_ENTRIES && !this.flushing) this.flushOnThreshold();
    }

    /** Send the accrued sample and reset, then resume measuring the same route. */
    private flushOnThreshold(): void {
        this.flushing = true;
        try {
            const wasTracking = this.isTracking;
            this.pauseTracking('log_threshold'); // accumulate active time, isTracking → false
            this.sendDwellTime(this.currentUrl);
            this.resetCounters();
            if (wasTracking) this.resumeTracking('log_threshold');
        } finally {
            this.flushing = false;
        }
    }

    /* --- core state machine --- */
    private resumeTracking(reason: string): void {
        const now = Date.now();
        if (!this.isTracking) {
            this.lastStartTime = now;
            this.isTracking = true;
            this.addActivityLog('resume', reason, now);
            this.log('Resumed', {reason, lastStartTime: this.lastStartTime});
        }
        this.startInactivityCountdown();
    }

    private pauseTracking(reason: string): void {
        const now = Date.now();
        if (this.isTracking) {
            const delta = now - this.lastStartTime;
            this.activeTime += delta;
            this.isTracking = false;
            this.addActivityLog('pause', reason, now, delta);
            this.log('Paused', {reason, deltaMs: delta, total: this.activeTime});
        }
        this.clearInactivityCountdown();
    }

    private startInactivityCountdown(): void {
        this.clearInactivityCountdown();
        // Run the timer outside Angular so its firing never triggers change detection.
        this.ngZone.runOutsideAngular(() => {
            this.inactivityTimer = window.setTimeout(() => this.pauseTracking('inactivity'), INACTIVITY_MS);
        });
    }

    private clearInactivityCountdown(): void {
        if (this.inactivityTimer != null) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }

    /* --- visibility / focus / activity listeners --- */
    private setupListeners(): void {
        // Register on `target` and remember how to remove it again on destroy.
        // (AbortController's `signal` option is avoided — it breaks under zone.js + jsdom.)
        const on = (target: EventTarget, type: string, handler: EventListener, opts?: AddEventListenerOptions): void => {
            target.addEventListener(type, handler, opts);
            this.teardown.push(() => target.removeEventListener(type, handler, opts));
        };

        this.ngZone.runOutsideAngular(() => {
            on(document, 'visibilitychange', () => {
                if (document.hidden || document.visibilityState !== 'visible') {
                    this.pauseTracking('visibilitychange');
                    this.sendDwellTime(this.currentUrl);
                } else {
                    this.resumeTracking('visibilitychange');
                }
            }, {passive: true});

            on(window, 'blur', () => this.pauseTracking('blur'));
            on(window, 'focus', () => {
                if (!document.hidden && document.visibilityState === 'visible') this.resumeTracking('focus');
            });

            const onActivity = (type: string): EventListener => () => {
                if (!document.hidden && document.visibilityState === 'visible') this.resumeTracking(type);
            };
            const opts: AddEventListenerOptions = {passive: true};
            on(document, 'scroll', onActivity('scroll'), opts);
            on(document, 'click', onActivity('click'), opts);
            on(document, 'keydown', onActivity('keydown'), opts);
            on(document, 'touchstart', onActivity('touchstart'), opts);
            on(document, 'touchmove', onActivity('touchmove'), opts);
            on(document, 'wheel', onActivity('wheel'), opts);
        });
    }

    /* --- dwell-time math + sending --- */
    private getCurrentDwellTime(): number {
        const now = Date.now();
        let total = this.activeTime;
        if (this.isTracking) total += now - this.lastStartTime;
        return Math.floor(total / 1000);
    }

    private resetCounters(): void {
        this.activeTime = 0;
        this.activityLog = [];
        this.lastStartTime = 0;
        this.addActivityLog('reset_continue', 'after_send', Date.now());
    }

    private sendDwellTime(url: string): void {
        const dwellTime = this.getCurrentDwellTime();
        if (dwellTime <= 1) return;

        const payload = {
            url,
            dwellTime,
            sessionId: this.getSessionId(),
            token: this.currentIdToken,
            activityLog: this.activityLog,
            analytics: this.getAnalyticsData(),
            explicaireVisitorId: this.ensureVisitorId(),
        };

        // localhost: never send — log the payload for debugging.
        if (this.debug) {
            this.log('Debug mode: would send', payload);
            this.resetCounters();
            return;
        }

        // The endpoint is authenticated — drop samples we can't authorize (e.g. login page).
        if (!this.currentIdToken) return;

        // Send as a plain string so the request stays a CORS-simple POST (text/plain),
        // avoiding a preflight that sendBeacon cannot perform. The function JSON-parses it.
        const body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
            const ok = navigator.sendBeacon(API_URL, body);
            if (ok) this.resetCounters();
        } else {
            fetch(API_URL, {method: 'POST', body, keepalive: true})
                .finally(() => this.resetCounters());
        }
    }

    private getSessionId(): string {
        let id = sessionStorage.getItem('dwell_session_id');
        if (!id) {
            id = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
            sessionStorage.setItem('dwell_session_id', id);
        }
        return id;
    }

    private ensureVisitorId(): string {
        const readCookie = (name: string): string | null => {
            const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
            return m ? decodeURIComponent(m[1]) : null;
        };
        const cookieDomain = this.debug ? null : COOKIE_DOMAIN;

        const writeCookie = (name: string, value: string): void => {
            const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
            const domainPart = cookieDomain ? `; domain=${cookieDomain}` : '';
            document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}${domainPart}; path=/; SameSite=Lax`;
        };

        let visitorId = readCookie(VISITOR_COOKIE) ?? localStorage.getItem(VISITOR_COOKIE);
        if (!visitorId) {
            visitorId = `visitor_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
            writeCookie(VISITOR_COOKIE, visitorId);
            try {
                localStorage.setItem(VISITOR_COOKIE, visitorId);
            } catch {
                /* ignore */
            }
        }
        return visitorId;
    }

    private getAnalyticsData(): AnalyticsData {
        const safe = <T>(fn: () => T): T | string => {
            try {
                const v = fn();
                return v ?? 'unknown';
            } catch {
                return 'unknown';
            }
        };
        return {
            userAgent: safe(() => navigator.userAgent) as string,
            language: safe(() => navigator.language) as string,
            languages: safe(() => navigator.languages ?? [navigator.language]) as readonly string[] | string,
            platform: safe(() => navigator.platform) as string,
            cookieEnabled: safe(() => navigator.cookieEnabled),
            screenWidth: safe(() => screen?.width),
            screenHeight: safe(() => screen?.height),
            devicePixelRatio: safe(() => window.devicePixelRatio),
            viewportWidth: safe(() => window.innerWidth),
            viewportHeight: safe(() => window.innerHeight),
            hardwareConcurrency: safe(() => navigator.hardwareConcurrency),
            touchSupported: safe(() =>
                'ontouchstart' in window ||
                (navigator.maxTouchPoints != null && navigator.maxTouchPoints > 0)),
            timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone) as string,
            referrer: safe(() => document.referrer) as string,
        };
    }
}
